/* eslint-disable @typescript-eslint/no-unsafe-return */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import { MeetingApp, SetupTestApp } from '@exploring-infinities/meet';
import type { ComponentType, ReactNode } from 'react';
import { Navigate, RouteObject, RouterProvider } from 'react-router';

import { AppFallback, AuthFallback } from '@/components/errors';
import { paths } from '@/config/paths';
// Use DataDog's createBrowserRouter for automatic route tracking
// This wraps react-router's createBrowserRouter with RUM view tracking
import { createBrowserRouter } from '@/instrument';
import { useGoogleLoginTracker } from '@/lib/analytics';

import { ProtectedRoute, PublicRoute } from './route-guards';
import { ProtectedRouteWv } from './route-guards-wv';
import { MaverickLayout } from './routes/app/maverick';
import AppRoot from './routes/app/root';
import AuthRoot from './routes/auth/root';

const convert = (m: any) => {
  const { default: Component, ...rest } = m;
  return {
    ...rest,
    Component,
  };
};

type LazyImport = () => Promise<{ default: ComponentType }>;

// Collapse the repeated "import a default Component and wrap it in a
// route guard" boilerplate into a single helper per guard. The dynamic
// import() string literal stays at the call site so bundler
// code-splitting still resolves each chunk statically.
const guardedLazy =
  (Guard: ComponentType<{ children: ReactNode }>) =>
  (load: LazyImport) =>
  async () => {
    const { default: Component } = await load();
    return {
      element: (
        <Guard>
          <Component />
        </Guard>
      ),
    };
  };

const protectedLazy = guardedLazy(ProtectedRoute);
const protectedWvLazy = guardedLazy(ProtectedRouteWv);

const baseRoutes: RouteObject[] = [
  {
    path: paths.auth.root.path,
    element: (
      <PublicRoute>
        <AuthRoot />
      </PublicRoute>
    ),
    ErrorBoundary: AuthFallback,
    children: [
      {
        index: true,
        lazy: () => import('./routes/auth/login').then(convert),
      },
      {
        path: paths.auth.register.path,
        lazy: () => import('./routes/auth/register').then(convert),
      },
      {
        path: paths.auth.login.path,
        lazy: () => import('./routes/auth/login').then(convert),
      },
      {
        path: paths.auth.forgotPassword.path,
        lazy: () => import('./routes/auth/forgot-password').then(convert),
      },
    ],
  },
  {
    path: paths.app.root.path,
    element: (
      <ProtectedRoute>
        <AppRoot />
      </ProtectedRoute>
    ),
    ErrorBoundary: AppFallback,
    children: [
      {
        index: true,
        lazy: () => import('./routes/app/dashboard').then(convert),
      },
      {
        path: paths.app.modules.path,
        lazy: () => import('./routes/app/modules').then(convert),
      },
      {
        path: paths.app.brainGym.path,
        lazy: () => import('./routes/app/brain-gym').then(convert),
      },
      {
        path: paths.app.moduleReports.path,
        lazy: () => import('./routes/app/module-reports').then(convert),
      },
      {
        path: paths.app.profiles.path,
        lazy: () => import('./routes/app/profiles').then(convert),
      },
      {
        path: paths.profile.select.path,
        lazy: () => import('./routes/app/profile/select').then(convert),
      },
      {
        path: paths.app.onboarding.path,
        lazy: () => import('./routes/app/onboarding').then(convert),
      },
      {
        path: paths.app.referralInd.path,
        lazy: () => import('./routes/app/referral-ind').then(convert),
      },
    ],
  },
];

const maverickRoute = paths.maverick
  ? [
      {
        path: paths.maverick.class.path,
        element: (
          <ProtectedRoute>
            <MaverickLayout />
          </ProtectedRoute>
        ),
        children: [
          {
            path: paths.maverick.class.setup.path,
            element: <SetupTestApp />,
          },
          {
            path: paths.maverick.class.meeting.path,
            element: <MeetingApp key="main" />,
          },
          {
            path: paths.maverick.class.parentMeeting.path,
            element: <MeetingApp key="breakout" />,
          },
        ],
      },
    ]
  : [];

const oldAppRedirectRoutes = [
  {
    path: paths.oldApp.assessment.path,
    lazy: () => import('./routes/old-app-redirect').then(convert),
  },
  {
    path: paths.oldApp.quiz.path,
    lazy: () => import('./routes/old-app-redirect').then(convert),
  },
  {
    path: paths.oldApp.reportRaw.path,
    lazy: () => import('./routes/old-app-redirect').then(convert),
  },
  {
    path: paths.oldApp.quizJoin.path,
    lazy: () => import('./routes/old-app-redirect').then(convert),
  },
];

const legacyRouteRedirects: RouteObject[] = [
  {
    path: paths.oldApp.selectProfile.path,
    element: <Navigate to={paths.profile.select.path} />,
  },
];

const standaloneRoutes: RouteObject[] = [
  {
    path: paths.app.videoPlayer.path,
    lazy: protectedLazy(() => import('./routes/app/video-player')),
  },
  {
    path: paths.app.batchPausePolicy.path,
    lazy: async () => {
      const { default: Component } = await import(
        './routes/app/batch-pause-policy'
      );
      return { element: <Component /> };
    },
  },
  {
    path: paths.app.pcFeedback.path,
    lazy: protectedLazy(() => import('./routes/app/pc-feedback')),
  },
  {
    path: paths.app.parentConnectBook.path,
    lazy: protectedLazy(() => import('./routes/app/parent-connect-book')),
  },
  {
    path: paths.app.parentConnectRespond.path,
    lazy: protectedLazy(() => import('./routes/app/parent-connect-respond')),
  },
  {
    path: paths.app.parentConnectEditRequest.path,
    lazy: protectedLazy(
      () => import('./routes/app/parent-connect-edit-request')
    ),
  },
  {
    path: paths.app.parentConnectCancelReschedule.path,
    lazy: protectedLazy(
      () => import('./routes/app/parent-connect-cancel-reschedule')
    ),
  },
  {
    path: paths.app.parentConnectCancelRebook.path,
    lazy: protectedLazy(
      () => import('./routes/app/parent-connect-cancel-rebook')
    ),
  },
  {
    path: paths.app.parentConnectSessions.path,
    lazy: protectedLazy(() => import('./routes/app/parent-connect-sessions')),
  },
  {
    path: paths.app.pcPtmSessions.path,
    lazy: protectedLazy(() => import('./routes/app/pc-ptm-sessions')),
  },
  {
    path: paths.app.brainGymWv.path,
    lazy: protectedWvLazy(() => import('./routes/app/brain-gym-wv')),
  },
  {
    path: paths.app.moduleReportsWv.path,
    lazy: protectedWvLazy(() => import('./routes/app/module-reports-wv')),
  },
  {
    path: paths.app.referralIndWv.path,
    lazy: protectedWvLazy(() => import('./routes/app/referral-ind')),
  },
];

const notFoundRoute = {
  path: '*',
  lazy: () => import('./routes/not-found').then(convert),
};

export const router = createBrowserRouter([
  ...baseRoutes,
  ...maverickRoute,
  ...standaloneRoutes,
  ...oldAppRedirectRoutes,
  ...legacyRouteRedirects,
  notFoundRoute,
]);

export const AppRouter = () => {
  // Track Google login success/failure after OAuth redirect
  useGoogleLoginTracker();

  return <RouterProvider router={router} />;
};
